<template>
    <TailwindLayout title="Email Previews">
        <div class="-mx -mt-10 space-y-6">
            <!-- Header band -->
            <div class="relative overflow-hidden rounded-2xl border border-rent-risk-blue-100 bg-gradient-to-br from-rent-risk-blue-100 via-white to-white px-6 py-7 sm:px-8">
                <Link :href="route('bridge.index')" class="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-rent-risk-blue-400 hover:opacity-80">
                    <font-awesome-icon :icon="faChevronLeft" class="h-3 w-3" />
                    The Bridge
                </Link>
                <div class="flex items-center gap-4">
                    <span class="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-rent-risk-blue-400 text-white shadow-sm">
                        <font-awesome-icon :icon="faEnvelopeOpenText" class="h-7 w-7" />
                    </span>
                    <div>
                        <h1 class="text-3xl font-bold tracking-tight text-rent-risk-blue-400">Email Previews</h1>
                        <p class="mt-1 text-sm text-gray-600">
                            Every transactional email, rendered with placeholder data. Values like
                            <code class="rounded bg-white px-1 py-0.5 text-xs text-gray-500">{user-&gt;name}</code>
                            mark where real data appears when sent. Nothing here sends mail.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Controls -->
            <div class="space-y-3">
                <input
                    v-model="search"
                    type="search"
                    placeholder="Search emails…"
                    class="w-full rounded-lg border-gray-300 text-sm shadow-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400 sm:max-w-sm"
                />
                <div class="flex flex-wrap gap-2">
                    <button
                        v-for="tab in filterTabs"
                        :key="tab.key"
                        type="button"
                        class="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors"
                        :class="activeCategory === tab.key ? 'bg-rent-risk-blue-400 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
                        @click="activeCategory = tab.key"
                    >
                        {{ tab.label }}<span class="text-xs" :class="activeCategory === tab.key ? 'text-white/70' : 'text-gray-400'">{{ tab.count }}</span>
                    </button>
                </div>
            </div>

            <!-- Two-pane: list + preview -->
            <div class="grid grid-cols-1 gap-5 lg:grid-cols-[360px_1fr]">
                <!-- List -->
                <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div class="max-h-[70vh] divide-y divide-gray-100 overflow-y-auto">
                        <button
                            v-for="email in filteredEmails"
                            :key="email.key"
                            type="button"
                            class="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
                            :class="selected && selected.key === email.key ? 'bg-rent-risk-blue-100/60' : ''"
                            @click="select(email)"
                        >
                            <div class="min-w-0">
                                <p class="truncate text-sm font-semibold text-gray-900">{{ email.name }}</p>
                                <p class="truncate text-xs text-gray-400">{{ email.key }}</p>
                            </div>
                            <span class="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium" :class="categoryClass(email.category)">{{ email.category }}</span>
                        </button>
                        <p v-if="!filteredEmails.length" class="px-4 py-8 text-center text-sm text-gray-400">No emails match.</p>
                    </div>
                </div>

                <!-- Preview -->
                <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div v-if="selected" class="flex items-center justify-between border-b border-gray-100 px-5 py-3">
                        <div class="min-w-0">
                            <p class="truncate text-sm font-semibold text-gray-900">{{ selected.name }}</p>
                            <p class="truncate text-xs text-gray-400">{{ selected.category }}</p>
                        </div>
                        <a :href="rawUrl(selected)" target="_blank" rel="noopener" class="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                            <font-awesome-icon :icon="faArrowUpRightFromSquare" class="h-3.5 w-3.5" />
                            Open
                        </a>
                    </div>
                    <template v-if="selected">
                        <div v-if="previewLoading" class="flex h-[70vh] items-center justify-center text-sm text-gray-400">Loading preview…</div>
                        <div v-else-if="previewError" class="flex h-[70vh] flex-col items-center justify-center gap-2 text-center text-sm text-red-500">
                            <p>Couldn't load this preview.</p>
                            <a :href="rawUrl(selected)" target="_blank" rel="noopener" class="text-rent-risk-blue-400 underline">Try opening it directly</a>
                        </div>
                        <iframe
                            v-else
                            :key="selected.key"
                            :srcdoc="previewHtml"
                            title="Email preview"
                            class="h-[70vh] w-full bg-gray-50"
                        ></iframe>
                    </template>
                    <div v-else class="flex h-[70vh] flex-col items-center justify-center gap-3 text-center text-gray-400">
                        <font-awesome-icon :icon="faEnvelopeOpenText" class="h-10 w-10" />
                        <p class="text-sm">Select an email to preview it.</p>
                    </div>
                </div>
            </div>
        </div>
    </TailwindLayout>
</template>

<script setup>
import { computed, ref } from 'vue'
import axios from 'axios'
import { Link } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
    faEnvelopeOpenText,
    faChevronLeft,
    faArrowUpRightFromSquare,
} from '@fortawesome/pro-light-svg-icons'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'

const props = defineProps({
    emails: { type: Array, default: () => [] },
    categories: { type: Array, default: () => [] },
})

const search = ref('')
const activeCategory = ref('all')
const selected = ref(null)
const previewHtml = ref('')
const previewLoading = ref(false)
const previewError = ref(false)

const filteredEmails = computed(() => {
    const term = search.value.trim().toLowerCase()

    return props.emails.filter((email) => {
        const matchesCategory = activeCategory.value === 'all' || email.category === activeCategory.value
        const matchesSearch = !term
            || email.name.toLowerCase().includes(term)
            || email.key.toLowerCase().includes(term)

        return matchesCategory && matchesSearch
    })
})

const filterTabs = computed(() => {
    const tabs = [{ key: 'all', label: 'All', count: props.emails.length }]

    props.categories.forEach((category) => {
        tabs.push({
            key: category,
            label: category,
            count: props.emails.filter((email) => email.category === category).length,
        })
    })

    return tabs
})

async function select(email) {
    selected.value = email
    previewLoading.value = true
    previewError.value = false
    previewHtml.value = ''

    try {
        // Fetch the HTML and render it via the iframe's srcdoc rather than src:
        // srcdoc is immune to X-Frame-Options and http/https mixed-content, both
        // of which can block a framed URL navigation behind a proxy.
        const { data } = await axios.get(rawUrl(email), { headers: { Accept: 'text/html' } })
        if (selected.value?.key === email.key) {
            previewHtml.value = typeof data === 'string' ? data : String(data)
        }
    } catch (e) {
        if (selected.value?.key === email.key) {
            previewError.value = true
        }
    } finally {
        if (selected.value?.key === email.key) {
            previewLoading.value = false
        }
    }
}

function rawUrl(email) {
    // Relative path (strip origin) so the request stays same-origin and can't
    // trip an http-vs-https mismatch when APP_URL scheme differs behind a proxy.
    return route('bridge.email-previews.raw', { mailable: email.key }).replace(/^https?:\/\/[^/]+/, '')
}

const categoryPalette = {
    Lease: 'bg-purple-100 text-purple-700',
    Maintenance: 'bg-orange-100 text-orange-700',
    Enterprise: 'bg-amber-100 text-amber-700',
    Blog: 'bg-pink-100 text-pink-700',
    Reference: 'bg-emerald-100 text-emerald-700',
    'Application & Screening': 'bg-sky-100 text-sky-700',
    'Subscription & Billing': 'bg-indigo-100 text-indigo-700',
    'Rent & Payments': 'bg-green-100 text-green-700',
    'Invitations & Onboarding': 'bg-teal-100 text-teal-700',
    'Compliance & Verification': 'bg-rose-100 text-rose-700',
    Property: 'bg-blue-100 text-blue-700',
    'Admin & Reports': 'bg-slate-100 text-slate-700',
    'Account & Lifecycle': 'bg-gray-100 text-gray-600',
}

function categoryClass(category) {
    return categoryPalette[category] || 'bg-gray-100 text-gray-600'
}
</script>
